/**
 * Shared navigation item component.
 *
 * Used by both the primary app sidebar and settings sidebar to ensure
 * consistent typography, spacing, and interaction states.
 *
 * Based on the active NavItem in components/navigation/NavItem.tsx.
 */

import type { ComponentType } from 'react';
import { Link as AriaLink } from 'react-aria-components';

import { cx, sortCx } from '@/utils/cx';

const styles = sortCx({
  root: 'group relative flex w-full cursor-pointer items-center gap-2 rounded-md bg-primary outline-focus-ring transition duration-100 ease-linear select-none hover:bg-primary_hover focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2',
  rootSelected: 'bg-active hover:bg-secondary_hover',
});

interface NavItemProps {
  /** Display label. */
  label: string;
  /** URL to navigate to. */
  href?: string;
  /** Icon component to display. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: ComponentType<any>;
  /** Whether this item is a child (indented). */
  isChild?: boolean;
  /** Whether this item is the currently active route. */
  current?: boolean;
  /** Handlers for prefetching content on user intent. */
  onPrefetch?: {
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onMouseDown?: () => void;
  };
  /** Extra content rendered after the label (badges, shortcuts, chevrons). */
  trailing?: React.ReactNode;
  /** Tailwind background color class for pipeline icon (e.g. 'bg-brand-600'). */
  pipelineColorClass?: string;
}

export function NavItem({
  label,
  href,
  icon: Icon,
  isChild,
  current,
  onPrefetch,
  trailing,
  pipelineColorClass,
}: NavItemProps) {
  const iconElement =
    Icon &&
    (pipelineColorClass ? (
      <div
        className={cx(
          'flex size-5 shrink-0 items-center justify-center rounded-full',
          pipelineColorClass
        )}
      >
        <Icon aria-hidden='true' className='size-3 text-white' />
      </div>
    ) : (
      <Icon
        aria-hidden='true'
        className='size-3.5 shrink-0 text-fg-quaternary transition-inherit-all'
      />
    ));

  const labelElement = (
    <span className='flex-1 truncate text-sm font-semibold text-secondary transition-inherit-all group-hover:text-secondary_hover'>
      {label}
    </span>
  );

  const isExternal = href?.startsWith('http');

  return (
    <AriaLink
      href={href}
      target={isExternal ? '_blank' : '_self'}
      rel='noopener noreferrer'
      className={cx(
        // min-h-9 locks row height so trailing content (badges, shortcuts)
        // can never push the row taller and shift adjacent items.
        'min-h-9 px-3 py-[7px]',
        isChild && 'pl-7',
        styles.root,
        current && styles.rootSelected
      )}
      onMouseEnter={onPrefetch?.onMouseEnter}
      onMouseLeave={onPrefetch?.onMouseLeave}
      onMouseDown={onPrefetch?.onMouseDown}
      aria-current={current ? 'page' : undefined}
    >
      {iconElement}
      {labelElement}
      {trailing}
    </AriaLink>
  );
}
